import { useState, useEffect } from 'react';
import { getSampleDecisionResult } from '../api/decisionEngine';
import type { DecisionEngineResult } from '../api/decisionEngine';
import { DecisionTable, CumulativeCostChart, AnnualCostChart } from '@system-rec/decision-ui';
import type { DecisionTableRow, CumulativeCostDataPoint, AnnualCostBarData } from '@system-rec/decision-ui';
import type { RecommendationOption } from '@core/types';

function DecisionPage() {
  const [result, setResult] = useState<DecisionEngineResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getSampleDecisionResult()
      .then(setResult)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="page decision-page">
        <div className="page-header">
          <h2>Decision Analysis</h2>
          <p>Loading decision analysis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page decision-page">
        <div className="page-header">
          <h2>Decision Analysis</h2>
          <p className="error">Error loading data: {error}</p>
        </div>
      </div>
    );
  }

  if (!result) {
    return null;
  }

  const { output, costOverTimeData, annualCostData } = result;

  // Build table rows
  const buildTableRows = (): DecisionTableRow[] => {
    const rows: DecisionTableRow[] = [];

    // Add baseline if available
    if (output.baselineResult) {
      const lifespanData = output.baselineResult.longevity?.remainingLifespan_years;
      rows.push({
        optionName: 'Do Nothing (Current System)',
        lifespan: lifespanData && lifespanData.min != null && lifespanData.max != null
          ? `${lifespanData.min}-${lifespanData.max} years`
          : '—',
        replacements: output.baselineResult.longevity?.expectedReplacementsInPeriod || 0,
        annualCost_gbp: output.baselineResult.annualizedCost_gbp,
        totalCost_gbp: output.baselineResult.totalCost_gbp,
        deltaVsBaseline_gbp: 0,
        isBaseline: true,
      });
    }

    // Add primary and alternatives
    const allOptions: RecommendationOption[] = [output.primary, ...output.alternatives];
    
    allOptions.forEach((opt) => {
      // Include options that have either lcoo or deltaVsBaseline data
      if (opt.lcoo || opt.deltaVsBaseline) {
        const lifespanData = opt.longevity?.expectedLifespanYears;
        rows.push({
          optionName: opt.title,
          lifespan: lifespanData && lifespanData.min != null && lifespanData.max != null
            ? `${lifespanData.min}-${lifespanData.max} years`
            : '—',
          replacements: opt.longevity?.replacementsInPeriod || 0,
          annualCost_gbp: opt.deltaVsBaseline?.optionAnnualized_gbp || opt.lcoo?.annualRunningGBP || 0,
          totalCost_gbp: opt.lcoo?.totalCost_gbp || 0,
          deltaVsBaseline_gbp: opt.deltaVsBaseline ? -opt.deltaVsBaseline.totalSaving_gbp : 0,
        });
      }
    });

    return rows;
  };

  // Transform cost over time data to cumulative format
  const transformCostOverTimeData = (): CumulativeCostDataPoint[] => {
    if (!costOverTimeData) return [];
    
    const years = Math.max(...costOverTimeData.series.flatMap(s => s.data.map(d => d.year)));
    const result: CumulativeCostDataPoint[] = [];

    for (let year = 0; year <= years; year++) {
      const point: CumulativeCostDataPoint = { year };
      
      costOverTimeData.series.forEach((series) => {
        const dataPoint = series.data.find((d) => d.year === year);
        if (dataPoint) {
          point[series.id] = dataPoint.cost;
        }
      });
      
      result.push(point);
    }

    return result;
  };

  // Transform annual cost data to bar format  
  const transformAnnualCostData = (): AnnualCostBarData[] => {
    if (!annualCostData) return [];
    
    return annualCostData.bars.map((bar) => ({
      label: bar.label,
      cost: bar.annualCost_gbp,
      delta: bar.deltaVsBaseline_gbp || undefined,
      isBaseline: bar.isBaseline,
    }));
  };

  const tableRows = buildTableRows();
  const cumulativeData = transformCostOverTimeData();
  const annualBars = transformAnnualCostData();

  return (
    <div className="page decision-page">
      <div className="page-header">
        <h2>Decision Analysis</h2>
        <p className="page-subtitle">
          Complete comparison of all systems with cost projections
        </p>
      </div>

      <section className="decision-table-section">
        <h3>System Comparison</h3>
        <DecisionTable rows={tableRows} />
      </section>

      {cumulativeData.length > 0 && (
        <section className="charts-section">
          <div className="chart-container">
            <h3>Cumulative Cost Over Time</h3>
            <CumulativeCostChart data={cumulativeData} />
            <p className="chart-caption">
              Shows total cost accumulation over {costOverTimeData?.metadata.periodYears} years including upfront installation, 
              energy costs, and maintenance.
            </p>
          </div>
        </section>
      )}

      {annualBars.length > 0 && (
        <section className="charts-section">
          <div className="chart-container">
            <h3>Annual Cost Comparison</h3>
            <AnnualCostChart data={annualBars} />
            <p className="chart-caption">
              Year-by-year comparison of operating costs for each system option.
            </p>
          </div>
        </section>
      )}

      <section className="decision-summary">
        <div className="info-card">
          <h4>Making Your Decision</h4>
          <p>
            Consider both upfront costs and long-term savings when choosing your system. 
            The cumulative cost chart shows which systems provide better value over time, 
            while the annual cost comparison helps you understand yearly expenses.
          </p>
          {output.baselineResult && (
            <p>
              <strong>Note:</strong> All new systems are compared against your baseline 
              "do nothing" scenario (£{output.baselineResult.totalCost_gbp.toLocaleString()} over {output.baselineResult.periodYears} years).
            </p>
          )}
        </div>
      </section>
    </div>
  );
}

export default DecisionPage;
